import MediaItem from "./MediaItem";
import { Reply } from "lucide-react";

const replySenderLabel = (sender) => {
  if (sender === "doctor") return "Doctor";
  if (sender === "client") return "Patient";
  return "Message";
};

const replyPreviewText = (reply) => {
  const text = reply?.text || "";
  if (text) return text;
  if (reply?.mediaType) return "Media message";
  return "Original message";
};

const ChatBubble = ({
  message,
  isFirstInGroup,
  isLastInGroup,
  onReply,
  onOpenReply,
  highlighted,
}) => {
  const isDoctor = message.sender === "doctor";
  const reply = message.reply || message.replyTo || null;
  const replyId = message.replyId || reply?.replyId;

  const time =
    message.time ||
    (message.timestamp &&
      new Date(message.timestamp)
        .toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .toLowerCase());

  const text = message.text || message.message || "";

  return (
    <div
      className={`flex w-full ${
        isDoctor ? "justify-end" : "justify-start"
      } ${isLastInGroup ? "mb-4" : "mb-1"}`}
    >
      <div
        className={`flex max-w-[88%] flex-col sm:max-w-[72%] ${
          isDoctor ? "items-end" : "items-start"
        }`}
      >
        <div
          className={`group flex max-w-full items-end gap-1.5 ${
            isDoctor ? "flex-row-reverse" : "flex-row"
          }`}
        >
          {/* BUBBLE */}
          <div
            className={`
              relative min-w-0 space-y-2 px-4 py-2.5 text-sm leading-6 transition
              ${highlighted ? "ring-2 ring-amber-300 ring-offset-2" : ""}
              ${
                isDoctor
                  ? "bg-[#075E54] text-white"
                  : "border border-slate-200 bg-white text-slate-800"
              }
              ${
                isDoctor
                  ? `
                    rounded-l-2xl
                    ${isFirstInGroup ? "rounded-tr-2xl" : "rounded-tr-md"}
                    ${isLastInGroup ? "rounded-br-2xl" : "rounded-br-md"}
                  `
                  : `
                    rounded-r-2xl
                    ${isFirstInGroup ? "rounded-tl-2xl" : "rounded-tl-md"}
                    ${isLastInGroup ? "rounded-bl-2xl" : "rounded-bl-md"}
                  `
              }
            `}
          >
            {reply && (
              <button
                type="button"
                onClick={() => {
                  if (replyId && onOpenReply) onOpenReply(replyId);
                }}
                className={`w-full rounded-xl border-l-4 px-3 py-2 text-left transition ${
                  isDoctor
                    ? "border-white/60 bg-white/10 hover:bg-white/15"
                    : "border-[#075E54] bg-slate-50 hover:bg-slate-100"
                }`}
              >
                <span
                  className={`block text-[11px] font-semibold uppercase tracking-wide ${
                    isDoctor ? "text-white/80" : "text-slate-500"
                  }`}
                >
                  Reply to {replySenderLabel(reply.sender)}
                </span>

                <span
                  className={`mt-1 block line-clamp-2 text-xs leading-5 ${
                    isDoctor ? "text-white/90" : "text-slate-700"
                  }`}
                >
                  {replyPreviewText(reply)}
                </span>
              </button>
            )}

            {/* MEDIA */}
            {Array.isArray(message.media) &&
              message.media.length > 0 &&
              message.media.map((media, idx) => (
                <MediaItem key={idx} media={media} sender={message.sender} />
              ))}

            {/* TEXT */}
            {text && <p className="whitespace-pre-wrap break-words">{text}</p>}
          </div>

          {onReply && (
            <button
              type="button"
              onClick={() => onReply(message)}
              aria-label="Reply to message"
              title="Reply"
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 sm:opacity-0 sm:group-hover:opacity-100 ${
                isDoctor ? "border-emerald-100" : "border-slate-200"
              }`}
            >
              <Reply size={14} />
            </button>
          )}
        </div>

        {/* TIME */}
        {isLastInGroup && time && (
          <span
            className={`mt-1 text-[10px] text-slate-400 ${
              isDoctor ? "pr-1 text-right" : "pl-1 text-left"
            }`}
          >
            {time}
          </span>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
